import { ref } from "vue";
import { defineStore } from "pinia";
import { extractErrorMessage, handleInvalidToken } from "~/utils/apiError";

export const useDiscountStore = defineStore("discount", () => {
  const ordersStore = useOrdersStore();
  const { authFetch, hasToken, apiBase } = useAuthenticatedFetch();

  // --------------- State ---------------
  const discount = ref<Record<string, unknown> | false>(false);
  const discountError = ref<string | false>(false);
  const discountLoading = ref(false);
  const rewards = ref<unknown[] | false>(false);
  const rewardSelected = ref<unknown | false>(false);

  // --------------- Actions ---------------

  function setChosenDiscount(payload: Record<string, unknown> | false) {
    discount.value = payload;
  }

  function setDiscountError(payload: string | false) {
    discountError.value = payload;
  }

	const authStore = useAuthStore();

  // --------------- Internal Helpers ---------------

  function _authHeaders(): Record<string, string> {
    return {
      accept: "application/json",
      "x-access-token-customer": authStore.token!.token,
      "content-type": "application/json",
    };
  }

  async function addDiscount(payload: {
    id: string;
    discount_code: string;
		billing_method?: string;
    items?: { product_id: number; amount: number; modifiers: { modifier_id: number; quantity: number }[] }[];
    packages?: { package_id: number; amount: number }[];
  }) {
    discountLoading.value = true;
    discountError.value = false;

		const headers: Record<string, string> = authStore.isLoggedIn ? _authHeaders() : {};

    try {
      const response = await $fetch<{
        dataValues: Record<string, unknown>;
        paymentIntent: unknown;
        requires_payment?: boolean;
      }>(`${apiBase}/api/public/orders/applyDiscount/${payload.id}`, {
        method: "PUT",
        body: {
          discount_code: payload.discount_code,
					billing_method: payload.billing_method,
          ...(payload.items ? { items: payload.items } : {}),
          ...(payload.packages ? { packages: payload.packages } : {}),
        },
				headers
      });
      discountLoading.value = false;
      discount.value = {
        ...response.dataValues,
        discount_code: payload.discount_code,
      };
      ordersStore.setOrder({
				...ordersStore.order,
        ...response,
        maker: ordersStore.order ? (ordersStore.order as Record<string, unknown>).maker : undefined,
        orderProducts: ordersStore.order
          ? (ordersStore.order as Record<string, unknown>).orderProducts
          : undefined,
      } as unknown as import("./orders").Order);
    } catch (error: unknown) {
      discountLoading.value = false;
      // Surface the backend's specific reason (e.g. "Code expired", "Already used")
      // when present. Only fall back to the generic message when there's nothing
      // useful to show.
      const message = extractErrorMessage(error);
      discountError.value =
        message === "An unexpected error occurred" ? "Discount code not valid" : message;
    }
  }

  async function addReward(payload: { id: string; reward_id: string; discount_code: string }) {
    if (!hasToken()) return;

    discountLoading.value = true;
    discountError.value = false;

    try {
      const response = await authFetch<{
        dataValues: Record<string, unknown>;
        paymentIntent: unknown;
        requires_payment?: boolean;
      }>(`/api/customers/orders/applyReward/${payload.id}`, {
        method: "PUT",
        body: { reward_id: payload.reward_id },
      });
      discountLoading.value = false;
      rewardSelected.value = {
        ...response.dataValues,
        discount_code: payload.discount_code,
      };
      ordersStore.setOrder({
        ...response.dataValues,
        paymentIntent: response.paymentIntent,
        requires_payment: response.requires_payment ?? true,
        maker: ordersStore.order ? (ordersStore.order as Record<string, unknown>).maker : undefined,
        orderProducts: ordersStore.order
          ? (ordersStore.order as Record<string, unknown>).orderProducts
          : undefined,
      } as unknown as import("./orders").Order);
    } catch (error: unknown) {
      discountLoading.value = false;
      discountError.value = "Reward not valid";
      handleInvalidToken(error);
    }
  }

  async function getRewards() {
    if (!hasToken()) return;

    try {
      const response = await authFetch<unknown[]>("/api/customers/rewards");
      rewards.value = response;
    } catch (error: unknown) {
      handleInvalidToken(error);
    }
  }

  return {
    discount,
    discountError,
    discountLoading,
    rewards,
    rewardSelected,

    setChosenDiscount,
    setDiscountError,
    addDiscount,
    addReward,
    getRewards,
  };
});
